/**
 * Google Picker API を簡単に呼び出すためのユーティリティ。
 *
 * 使い方:
 *   import { openFolderPicker } from '../utils/googlePicker';
 *   const folder = await openFolderPicker({ accessToken });
 *   // folder = { id, name } or null（キャンセル時）
 *
 * 前提:
 *   - Google Cloud Console で Picker API を有効化
 *   - REACT_APP_GOOGLE_PICKER_API_KEY と REACT_APP_GOOGLE_OAUTH_CLIENT_ID を環境変数で設定
 */

const API_KEY = process.env.REACT_APP_GOOGLE_PICKER_API_KEY;
const APP_ID = (process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID || '').split('-')[0];

// Google API JS のロード状態を共有
let gapiLoadPromise = null;
let pickerReadyPromise = null;

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.defer = true;
    s.onload = resolve;
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
}

function ensureGapiLoaded() {
  if (gapiLoadPromise) return gapiLoadPromise;
  gapiLoadPromise = loadScript('https://apis.google.com/js/api.js');
  return gapiLoadPromise;
}

function ensurePickerLoaded() {
  if (pickerReadyPromise) return pickerReadyPromise;
  pickerReadyPromise = ensureGapiLoaded().then(() => new Promise((resolve) => {
    window.gapi.load('picker', { callback: resolve });
  }));
  return pickerReadyPromise;
}

/**
 * フォルダを選ばせる Picker を開く。選択されたフォルダ { id, name } を返す。
 * キャンセル時は null を返す。
 */
export async function openFolderPicker({ accessToken }) {
  if (!API_KEY) {
    throw new Error('REACT_APP_GOOGLE_PICKER_API_KEY が未設定です');
  }
  await ensurePickerLoaded();
  return new Promise((resolve) => {
    const view = new window.google.picker.DocsView(window.google.picker.ViewId.FOLDERS)
      .setIncludeFolders(true)
      .setSelectFolderEnabled(true)
      .setOwnedByMe(true)  // 共有アイテムを除外、自分が所有するフォルダのみ
      .setMimeTypes('application/vnd.google-apps.folder');

    const picker = new window.google.picker.PickerBuilder()
      .setAppId(APP_ID)
      .setOAuthToken(accessToken)
      .setDeveloperKey(API_KEY)
      .addView(view)
      .setTitle('Google Drive のフォルダを選択')
      .setCallback((data) => {
        const action = data[window.google.picker.Response.ACTION];
        if (action === window.google.picker.Action.PICKED) {
          const doc = data[window.google.picker.Response.DOCUMENTS][0];
          resolve({ id: doc.id, name: doc.name });
        } else if (action === window.google.picker.Action.CANCEL) {
          resolve(null);
        }
      })
      .build();
    picker.setVisible(true);
  });
}

/**
 * 画像ファイルを複数選択させる Picker を開く。
 * 範囲選択（Shift+Click）と個別追加（Ctrl/Cmd+Click）に対応。
 * 返り値: { files: [{ id, name }, ...] } もしくは null（キャンセル時）
 */
export async function openMultiImagePicker({ accessToken }) {
  if (!API_KEY) {
    throw new Error('REACT_APP_GOOGLE_PICKER_API_KEY が未設定です');
  }
  await ensurePickerLoaded();
  return new Promise((resolve) => {
    // 画像のみ表示 + フォルダナビゲーション可能 + 自分所有のもののみ
    const view = new window.google.picker.DocsView(window.google.picker.ViewId.DOCS_IMAGES)
      .setIncludeFolders(true)
      .setSelectFolderEnabled(false)  // フォルダは選択不可、ナビゲーションのみ
      .setOwnedByMe(true)  // 共有アイテムを除外、自分のドライブ内のみ
      .setMimeTypes('image/jpeg,image/png,image/bmp,image/webp');

    const picker = new window.google.picker.PickerBuilder()
      .setAppId(APP_ID)
      .setOAuthToken(accessToken)
      .setDeveloperKey(API_KEY)
      .addView(view)
      .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
      .setTitle('振り分け対象の写真を選択（Shift+Click で範囲選択、Ctrl/Cmd+Click で追加）')
      .setCallback((data) => {
        const action = data[window.google.picker.Response.ACTION];
        if (action === window.google.picker.Action.PICKED) {
          const docs = data[window.google.picker.Response.DOCUMENTS] || [];
          resolve({ files: docs.map(d => ({ id: d.id, name: d.name })) });
        } else if (action === window.google.picker.Action.CANCEL) {
          resolve(null);
        }
      })
      .build();
    picker.setVisible(true);
  });
}
